import PropTypes from "prop-types"
import React, { useCallback, useEffect } from "react"
import { createPortal } from "react-dom"
import { twMerge } from "tailwind-merge"

import { Button, CloseButton } from "./Buttons"
import Typography from "./Typography"

export const Box = ({ children, className, as = "div", keepBorderInMobile = false, ...rest }) => {
  const Tag = as

  return (
    <Tag
      {...rest}
      className={twMerge(
        "rounded-xl border border-gray-light bg-white p-8 md:p-6",
        keepBorderInMobile ? "" : "sm:border-none sm:p-0",
        className
      )}>
      {children}
    </Tag>
  )
}

export const EmptyStateBox = ({ children, className, ...rest }) => (
  <div
    {...rest}
    className={twMerge(
      "flex h-96 flex-col items-center justify-center rounded-xl bg-gray-ultralight p-6 text-center",
      className
    )}>
    <div className="max-w-[260px]">{children}</div>
  </div>
)

export const ProfilePageEmptyStateBox = ({
  children,
  title,
  className,
  onButtonClick,
  buttonCopy = "Add now",
  ...rest
}) => (
  <EmptyStateBox {...rest} className={twMerge("h-[438px]", className)}>
    <Typography variant="h4" as="h3">
      {title}
    </Typography>
    <Typography variant="subtitle">{children}</Typography>
    {onButtonClick && (
      <Button className="mt-4" onClick={onButtonClick}>
        {buttonCopy}
      </Button>
    )}
  </EmptyStateBox>
)

export const Banner = ({ children, className, type, ...rest }) => (
  <div
    {...rest}
    className={twMerge(
      "rounded-xl border border-gray-light bg-white p-4",
      type === "error"
        ? "border-red bg-red-light"
        : type === "warning"
        ? "border-orange bg-orange-light"
        : type === "info"
        ? "border-gray bg-gray-light"
        : type === "success"
        ? "border-green bg-green-light"
        : "",
      className
    )}>
    {children}
  </div>
)

Banner.propTypes = {
  type: PropTypes.oneOf(["error", "warning", "info", "success"])
}

export const Divider = ({ className }) => <div className={twMerge("my-8 h-px w-full bg-gray-light", className)} />

export const Flyout = ({
  visible,
  closeFlyout,
  header,
  children,
  footer,
  footerSticky = true,
  compactHeader = false
}) => {
  const escFunction = useCallback((event) => {
    if (event.key === "Escape") {
      closeFlyout()
    }
  }, [])

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false)

    return () => {
      document.removeEventListener("keydown", escFunction, false)
    }
  }, [])

  const panelClasses = twMerge(
    `fixed w-[496px] bg-white duration-500 md:w-screen ${
      visible ? "right-0 md:right-0" : "right-[-496px] md:-right-100vw"
    }`
  )

  return (
    <>
      {createPortal(
        <>
          <div
            onClick={closeFlyout}
            className={`fixed inset-0 z-50 bg-black/50 transition-opacity duration-500 ${
              visible ? "visible" : "hidden"
            }`}
          />
          <div
            // Fill viewport with top/bottom (not 100vh): sticky Save stays above Android/iOS system chrome
            className={`Flyout inset-y-0 z-[51] flex flex-col overflow-hidden ${panelClasses}`}
            style={{ overscrollBehaviorBlock: "contain" }}>
            <div
              className={twMerge(
                "z-1 flex shrink-0 justify-between border-b border-gray-light px-8",
                compactHeader ? "items-start py-4 md:px-6 md:py-4" : "items-center py-6 md:p-6"
              )}>
              <Typography
                variant="h4"
                as="h2"
                className={compactHeader ? "min-w-0 flex-1 pr-2 leading-tight" : undefined}>
                {header}
              </Typography>
              <CloseButton className={compactHeader ? "mt-0.5 shrink-0" : undefined} onClick={closeFlyout} />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-8 md:p-6">
              {children}
              {footer && !footerSticky && <div className="mt-6 border-t border-gray-light pt-6">{footer}</div>}
            </div>
            {footer && footerSticky && (
              <div className="z-1 shrink-0 border-t border-gray-light p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                {footer}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  )
}
